import { Input, InputGroup, HStack, chakra } from '@chakra-ui/react'
import PropTypes from 'prop-types'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { standardLogin } from '../../redux/storeConfig/slices/slice.auth'

/**
 * Chakra UI component to style the MFA input
 */
const MFAChar = chakra(Input, {
  baseStyle: {
    fontSize: '2xl',
    px: 0,
    textAlign: 'center',
    w: 12,
    h: 12
  }
})

/**
 * Component to input a 6-digit MFA token
 *
 * @param {Object} props
 * @param {{email: string; password: string}} props.formData
 *
 * @returns {JSX.Element}
 */
export default function MFAInput({ formData }) {
  const dispatch = useDispatch()

  const inputsRef = useRef([])
  const [tokenChars, setTokenChars] = useState(Array.from({ length: 6 }))

  const tokenValue = useMemo(() => tokenChars.join(''), [tokenChars])

  const readyToSubmit = useMemo(
    () => tokenValue.length === 6 && formData.email && formData.password,
    [formData, tokenValue.length]
  )

  useEffect(() => {
    if (readyToSubmit) dispatch(standardLogin({ ...formData, token: tokenValue }))
  }, [dispatch, formData, readyToSubmit, tokenValue])

  const handleInputChange = e => {
    const value = e.target.value
    const nextSibling = e.target.nextSibling
    const previousSibling = e.target.previousSibling

    if (value.length === 1) {
      if (nextSibling) {
        nextSibling.focus()
      }
    } else if (value.length === 0) {
      if (previousSibling) {
        previousSibling.focus()
      }
    }
  }

  const handleInputFocus = e => {
    e.target.select()
  }

  const handlePaste = e => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text').slice(0, inputsRef.current.length)

    if (/^\d+$/.test(pastedData)) setTokenChars(pastedData.split(''))
    else setTokenChars(Array.from({ length: 6 }))

    // Populate inputs with pasted data
    pastedData.split('').forEach((char, index) => {
      if (inputsRef.current[index]) {
        inputsRef.current[index].value = char
      }
    })

    // Focus the next empty input
    const nextEmptyIndex = pastedData.length
    if (inputsRef.current[nextEmptyIndex]) {
      inputsRef.current[nextEmptyIndex].focus()
    }
  }

  return (
    <InputGroup as={HStack} w="fit-content" spacing={2} onPaste={handlePaste}>
      {tokenChars.map((_, index) => (
        <MFAChar
          key={index}
          maxLength={1}
          onChange={e => {
            if (!/^\d*$/.test(e.target.value)) return e.preventDefault()

            handleInputChange(e)
            setTokenChars(prev => {
              const newChars = [...prev]
              newChars[index] = e.target.value
              return newChars
            })
          }}
          value={_}
          onFocus={handleInputFocus}
          ref={el => (inputsRef.current[index] = el)} // Store a reference to each input
        />
      ))}
    </InputGroup>
  )
}

MFAInput.propTypes = {
  formData: PropTypes.shape({ email: PropTypes.string, password: PropTypes.string })
}
